import { useRouteMatcher, useRoutes, useUrlGenerator } from '@folklore/routes';
import queryString from 'query-string';

import { useHasPermission, useIsAdmin, useUserRole } from '../contexts/AuthContext';
import { useSite } from '../contexts/SiteContext';
import { documentTypeFromPath, pathFromCollectionType } from '../routes';
import { useAttributionQuery } from './useAttribution';

function queryToString(query) {
    return query !== null && Object.keys(query).length > 0
        ? `?${queryString.stringify(query)}`
        : '';
}

export function useDocumentTypeFromPath(path) {
    const routes = useRoutes();
    const routeMatcher = useRouteMatcher();
    if (path === null) {
        return null;
    }
    const [, { type: pathType = null } = {}] = routeMatcher(routes.document, path);
    return (pathType !== null ? documentTypeFromPath(pathType) : null) || pathType;
}

export function useDocumentUrlGenerator() {
    const url = useUrlGenerator();
    const attributionQuery = useAttributionQuery();
    const hasAttributionAccess = useHasPermission('attribution');
    const isAdmin = useIsAdmin();
    const role = useUserRole();
    const isStaff = role === 'staff';
    return (document, query = null) => {
        const { type = null, slug = null } = document || {};
        const { attribution = true, ...otherQuery } = query || {};
        const finalQuery = {
            ...otherQuery,
            ...(hasAttributionAccess && attribution && !isAdmin && !isStaff
                ? attributionQuery
                : null),
        };
        return type !== null && slug !== null
            ? `${url(type, { slug })}${queryToString(finalQuery)}`
            : null;
    };
}

export function useDocumentShareUrlGenerator() {
    const { handle: siteHandle } = useSite();
    const documentUrl = useDocumentUrlGenerator();
    const attributionQuery = useAttributionQuery();
    const hasAttributionAccess = useHasPermission('attribution');
    return (document, query = null) => {
        const { type = null, micromagNumber = null } = document || {};
        const { attribution = true, ...otherQuery } = query || {};
        const finalQuery = {
            ...otherQuery,
            ...(hasAttributionAccess && attribution ? attributionQuery : null),
        };
        let url = [
            `https://${siteHandle === 'france' ? 'urbania.fr' : 'urbania.ca'}`,
            documentUrl(document, finalQuery),
        ].join('');
        if (type === 'micromag' && micromagNumber != null) {
            url = [
                `https://${siteHandle === 'france' ? 'micromag.fr' : 'micromag.ca'}`,
                `/${micromagNumber}`,
                queryToString(finalQuery),
            ].join('');
        }
        return url;
    };
}

export function useCollectionUrlGenerator() {
    const url = useUrlGenerator();
    return (collection, query = null) => {
        const { type = null, slug = null } = collection || {};
        return slug !== null
            ? `${url('collection', { type: pathFromCollectionType(type), slug })}${queryToString(query)}`
            : null;
    };
}

export function useTaxonomyUrlGenerator() {
    const url = useUrlGenerator();
    return (taxonomy, subType = null, query = null) => {
        const { type = null, slug = null, brand } = taxonomy || {};
        const { slug: brandSlug = null } = brand || {};
        if (type === null || slug === null) {
            return null;
        }
        if (type === 'format' && slug === 'balado') {
            return url('podcasts');
        }
        if (type === 'section') {
            return `${url('section', {
                brand: brandSlug,
                section: slug,
                type: subType,
            })}${queryToString(query)}`;
        }
        return `${url(type, { slug, type: subType })}${queryToString(query)}`;
    };
}

export function useFormatUrlGenerator() {
    const url = useUrlGenerator();
    return (format, query = null) => {
        const { handle, slug = null } = format || {};
        return slug !== null
            ? `${handle === 'balado' || slug === 'balado' ? url('podcasts') : url('format', { slug })}${queryToString(query)}`
            : null;
    };
}

export function useCategoryUrlGenerator() {
    const url = useUrlGenerator();
    return (category, query = null) => {
        const { slug = null } = category || {};
        return slug !== null ? `${url('category', { slug })}${queryToString(query)}` : null;
    };
}

export function useTopicUrlGenerator() {
    const url = useUrlGenerator();
    return (topic, query = null) => {
        const { slug = null } = topic || {};
        return slug !== null ? `${url('topic', { slug })}${queryToString(query)}` : null;
    };
}

export function useAuthorUrlGenerator() {
    const url = useUrlGenerator();
    return (author, query = null) => {
        const { slug = null } = author || {};
        return slug !== null ? `${url('author', { author: slug })}${queryToString(query)}` : null;
    };
}

export function useCanonicalUrl() {
    const { handle: siteHandle } = useSite();
    return (url) =>
        url !== null && url.match(/^(https?)?:\/\//i) === null
            ? `https://${siteHandle === 'france' ? 'urbania.fr' : 'urbania.ca'}${url}`
            : url;
}
